import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, LineChart, Line, ComposedChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import SectionHeader from "../components/ui/SectionHeader";
import ChartCard from "../components/ui/ChartCard";
import MetricCard from "../components/ui/MetricCard";
import ChartTooltip from "../components/ui/ChartTooltip";
import { useTranslate } from "../i18n/useTranslate";
import { googleColor } from "../data/googleColors";
import { chartColor, gridProps, axisTick, legendStyle } from "../data/chartTheme";
import { STATUS, statusHex, statusClasses, thresholdStatus } from "../data/statusColors";
import {
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  Clock, Star, Target, ChevronDown, ChevronUp,
  Building2, Shield, Zap, Calendar,
} from "lucide-react";

// ── inline data ──────────────────────────────────────────────────
const DEPT_KPI = [
  { dept:"Police",     target:95, actual:91, budget:420, burn:312, staff:6420, incidents:18, sla:94, color:"#2563EB" },
  { dept:"Health",     target:95, actual:88, budget:180, burn:122, staff:1680, incidents:6,  sla:91, color:"#10B981" },
  { dept:"Transport",  target:90, actual:85, budget:140, burn:98,  staff:840,  incidents:4,  sla:88, color:"#F59E0B" },
  { dept:"Municipal",  target:88, actual:82, budget:200, burn:144, staff:2200, incidents:3,  sla:86, color:"#7C3AED" },
  { dept:"NDRF",       target:100,actual:96, budget:80,  burn:52,  staff:320,  incidents:2,  sla:98, color:"#EF4444" },
  { dept:"Revenue",    target:85, actual:80, budget:60,  burn:38,  staff:480,  incidents:1,  sla:84, color:"#64748B" },
];

const BUDGET_CATEGORIES = [
  { cat:"Disaster Management",   allocated:240, spent:162, pct:68 },
  { cat:"Police Modernization",  allocated:180, spent:128, pct:71 },
  { cat:"Governance IT",         allocated:120, spent:84,  pct:70 },
  { cat:"Command Centers",       allocated:160, spent:110, pct:69 },
  { cat:"Resilience Infra",      allocated:200, spent:132, pct:66 },
  { cat:"Emergency Systems",     allocated:140, spent:98,  pct:70 },
  { cat:"Public Safety",         allocated:100, spent:64,  pct:64 },
  { cat:"AI Governance",         allocated:60,  spent:36,  pct:60 },
];

const RISK_RADAR = [
  { domain:"Crowd",        score:78, threshold:60 },
  { domain:"Flood",        score:42, threshold:60 },
  { domain:"Health",       score:38, threshold:60 },
  { domain:"Transport",    score:55, threshold:60 },
  { domain:"Infrastructure",score:32, threshold:60 },
  { domain:"PublicOrder",  score:48, threshold:60 },
];

const RISK_TREND = [
  { time:"06:00", composite:38, crowd:42, flood:28, health:32 },
  { time:"08:00", composite:52, crowd:58, flood:30, health:36 },
  { time:"10:00", composite:61, crowd:72, flood:33, health:39 },
  { time:"11:00", composite:68, crowd:80, flood:35, health:40 },
  { time:"12:00", composite:64, crowd:74, flood:38, health:42 },
  { time:"13:00", composite:59, crowd:68, flood:40, health:44 },
  { time:"14:00", composite:62, crowd:78, flood:42, health:46 },
];

const STRATEGIC_DECISIONS = [
  { id:"SD-44", time:"14:33", decision:"Approve crowd diversion plan — Ramkund Entry 2",           by:"DCP Nashik",      impact:"HIGH",   status:"Executed"  },
  { id:"SD-43", time:"14:26", decision:"Pre-position NDRF Platoon 2 at Camp 3",                    by:"Dist. Collector", impact:"HIGH",   status:"Executed"  },
  { id:"SD-42", time:"13:58", decision:"Activate overflow shelter OFZ-B — 138% capacity",          by:"Dist. Collector", impact:"HIGH",   status:"Active"    },
  { id:"SD-41", time:"13:44", decision:"CM convoy route POL-002 approved — 15:30 ingress",         by:"SP Nashik",       impact:"MED",    status:"Pending"   },
  { id:"SD-40", time:"13:30", decision:"Issue city-wide heat advisory — 6 languages",              by:"CMO Nashik",      impact:"MED",    status:"Executed"  },
  { id:"SD-39", time:"12:58", decision:"Budget supplementary ₹12 Cr — extra ambulance deployment", by:"CM Office",       impact:"HIGH",   status:"Approved"  },
  { id:"SD-38", time:"12:00", decision:"Mutual aid NDRF Aurangabad — 2 platoons requested",        by:"DM Nashik",       impact:"HIGH",   status:"Approved"  },
];

const READINESS = [
  { event:"11 Jul — Shravan Purnima",  days:32, crowd:"9L",  readiness:78, budget:82, staff:85, infra:76 },
  { event:"12 Aug — Simhastha Peak",   days:64, crowd:"12L", readiness:62, budget:68, staff:72, infra:60 },
  { event:"14 Sep — Anant Chaturdashi",days:97, crowd:"8L",  readiness:54, budget:60, staff:64, infra:52 },
];

const POLICY_COMPLIANCE = [
  { policy:"DPDP 2023 — Data Protection",       score:97, status:"Compliant"   },
  { policy:"Disaster Management Act 2005",       score:94, status:"Compliant"   },
  { policy:"Police Reforms — Transparency",      score:88, status:"Compliant"   },
  { policy:"RTI Compliance",                     score:91, status:"Compliant"   },
  { policy:"Environmental Clearances",           score:95, status:"Compliant"   },
  { policy:"Labour Safety — Crowd Workers",      score:84, status:"Review"      },
  { policy:"IT Act 2000 — Cyber Infrastructure", score:92, status:"Compliant"   },
];

const BUDGET_PIE = [
  { name:"Disaster Mgmt",    value:240, color:"#EF4444" },
  { name:"Police Modern.",   value:180, color:"#2563EB" },
  { name:"Command Centers",  value:160, color:"#7C3AED" },
  { name:"Resilience Infra", value:200, color:"#F59E0B" },
  { name:"Emergency Sys.",   value:140, color:"#F97316" },
  { name:"Governance IT",    value:120, color:"#10B981" },
  { name:"Public Safety",    value:100, color:"#0891B2" },
  { name:"AI Governance",    value:60,  color:"#64748B" },
];

const TOTAL_BUDGET = BUDGET_CATEGORIES.reduce((a, b) => a + b.allocated, 0);
const TOTAL_SPENT  = BUDGET_CATEGORIES.reduce((a, b) => a + b.spent, 0);
const BURN_RATE_PCT = Math.round((TOTAL_SPENT / TOTAL_BUDGET) * 100);
const POLICY_SCORE_PCT = Math.round(
  POLICY_COMPLIANCE.reduce((a, p) => a + p.score, 0) / POLICY_COMPLIANCE.length
);
// "Decisions Today" is derived from the newest decision's own running ID
// (SD-44 = the 44th decision logged today) rather than a separately typed
// number, so it can't drift from the log rendered below it.
const DECISIONS_TODAY = Number(STRATEGIC_DECISIONS[0].id.split("-")[1]);

// Decision "impact" and "status" vocabularies mapped onto the shared status
// bucket set so their pill colors stay consistent with the rest of the app.
const IMPACT_BUCKET = { HIGH: "serious", MED: "warning" };
const impactBucket = v => IMPACT_BUCKET[v] || "info";

const DECISION_STATUS_BUCKET = { Executed: "good", Approved: "info", Active: "warning" };
const decisionStatusBucket = s => DECISION_STATUS_BUCKET[s] || "neutral";

// Department -> the page that owns its operational detail, where one
// exists. Municipal/Revenue don't have a single dedicated page yet.
const DEPT_ROUTE = { Police: "/police-modern", Health: "/health", Transport: "/mobility", NDRF: "/disaster-mgmt" };

// ── component ────────────────────────────────────────────────────
export default function ExecutiveIntelligence() {
  const [expandedDecision, setExpandedDecision] = useState(null);
  const t = useTranslate();

  return (
    <div className="space-y-8">
      <SectionHeader icon="BarChart2" title="Executive Decision Intelligence"
        subtitle="CM & Collector strategic command · budget burn · department KPIs · risk heatmap · Shahi Snan readiness · policy compliance · decision log · Maha AI governance layer — Simhastha 2027"
        badge="CM LIVE"
      />

      {/* KPIs — budget/policy/decision figures are computed from the same
          arrays rendered further down the page (BUDGET_CATEGORIES,
          POLICY_COMPLIANCE, STRATEGIC_DECISIONS) instead of being retyped,
          so they can't quietly drift out of sync with their own detail views. */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <MetricCard title="Budget Burn"        value={`₹${TOTAL_SPENT} Cr`}    icon="TrendingUp"    color="blue"   index={0} />
        <MetricCard title="Total Allocated"    value={`₹${TOTAL_BUDGET.toLocaleString()} Cr`} icon="Target"        color="green"  index={1} />
        <MetricCard title="Burn Rate"          value={`${BURN_RATE_PCT}%`}     icon="Activity"      color="amber"  index={2} />
        <MetricCard title="Risk Index"         value="62 / 100"   icon="AlertTriangle" color="amber"  index={3} live />
        <MetricCard title="Modules Live"       value="20"         icon="Zap"           color="green"  index={4} />
        <MetricCard title="Decisions Today"    value={`${DECISIONS_TODAY}`}    icon="CheckCircle"   color="blue"   index={5} />
        <MetricCard title="Policy Score"       value={`${POLICY_SCORE_PCT}%`}  icon="Shield"        color="green"  index={6} />
        <MetricCard title="Next Shahi Snan"    value="32 days"    icon="Calendar"      color="purple" index={7} />
      </div>

      {/* Executive Banner */}
      <motion.div initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }}
        className="rounded-2xl bg-white border border-slate-200 shadow-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-emerald-400 live-dot" />
              <span className="text-[10px] font-black text-saffron-dark tracking-widest">
                {t("MAHA AI — EXECUTIVE INTELLIGENCE · CM OFFICE CONNECTED · 14:35 IST")}
              </span>
            </div>
            <h3 className="text-base font-black text-slate-900">
              {t("Simhastha 2027 — Mission-Critical Governance Command")}
            </h3>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              {t("6 departments · ₹808 Cr burn (67%) · composite risk 62/100 · 20 Maha Kumbh AI modules live · 44 decisions today · 7 policy frameworks compliant · Next Shahi Snan: 32 days")}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {DEPT_KPI.slice(0,4).map((d, i) => {
              const c = googleColor(i);
              return (
                <div key={d.dept}
                  className="rounded-xl px-2.5 py-2 text-center min-w-[72px] shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                  style={{ background:c.bgLight, border:`1px solid ${c.border}` }}>
                  <div className="text-[8px] mb-0.5 font-black uppercase tracking-wide" style={{ color:c.text }}>{t(d.dept)}</div>
                  <div className="text-sm font-black" style={{ color:c.text }}>{d.actual}%</div>
                  <div className="text-[8px] opacity-70 font-medium" style={{ color:c.text }}>{t("vs")} {d.target}% {t("target")}</div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Risk Radar + Risk Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Multi-Domain Risk Heatmap" subtitle="Composite risk score per domain — threshold 60" live>
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={RISK_RADAR} cx="50%" cy="50%" outerRadius="70%">
              <PolarGrid stroke={gridProps.stroke} />
              <PolarAngleAxis dataKey="domain" tick={axisTick} tickFormatter={t} />
              <PolarRadiusAxis angle={90} domain={[0,100]} tick={axisTick} />
              <Radar dataKey="threshold" stroke={statusHex("critical")} fill={statusHex("critical")} fillOpacity={0.05} strokeDasharray="4 2" name="Threshold" />
              <Radar dataKey="score"     stroke={chartColor(0)} fill={chartColor(0)} fillOpacity={0.2} name="Current Score" />
              <Legend wrapperStyle={legendStyle} formatter={(value) => t(value)} />
              <Tooltip content={<ChartTooltip />} />
            </RadarChart>
          </ResponsiveContainer>
          <Link to="/risk-intelligence" className="text-[10px] font-bold text-blue-700 hover:text-blue-800 mt-1 inline-block">
            {t("→ Full multi-hazard risk breakdown")}
          </Link>
        </ChartCard>

        <ChartCard title="Composite Risk Index — Hourly Trend"
          subtitle="Overall + crowd + flood + health risk curves across the day">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={RISK_TREND} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="time" tick={axisTick} />
              <YAxis domain={[0,100]} tick={axisTick} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} formatter={(value) => t(value)} />
              <Line dataKey="composite" stroke={chartColor(0)} strokeWidth={2.5} dot={false} name="Composite" />
              <Line dataKey="crowd"     stroke={chartColor(1)} strokeWidth={2}   dot={false} name="Crowd"     />
              <Line dataKey="flood"     stroke={chartColor(2)} strokeWidth={1.5} dot={false} name="Flood"     />
              <Line dataKey="health"    stroke={chartColor(3)} strokeWidth={1.5} dot={false} name="Health"    />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Department Performance Scorecard */}
      <ChartCard title="Department Performance Scorecard"
        subtitle="All 6 departments — target vs actual · budget burn · staff · SLA compliance">
        <div className="overflow-x-auto">
          <table className="gov-table w-full text-xs">
            <thead>
              <tr>
                <th className="text-left">{t("Department")}</th>
                <th className="text-center">{t("Target")}</th>
                <th className="text-center">{t("Actual")}</th>
                <th className="text-center">{t("Gap")}</th>
                <th>{t("Budget Burn")}</th>
                <th className="text-center">{t("Staff")}</th>
                <th className="text-center">{t("Incidents")}</th>
                <th className="text-center">{t("SLA %")}</th>
              </tr>
            </thead>
            <tbody>
              {DEPT_KPI.map((d, i) => {
                const gap = d.actual - d.target;
                const pct = Math.round((d.burn / d.budget) * 100);
                return (
                  <motion.tr key={d.dept} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.06 }}
                    className="border-l-2" style={{ borderLeftColor: d.color }}>
                    <td className="font-black text-slate-900">
                      {DEPT_ROUTE[d.dept]
                        ? <Link to={DEPT_ROUTE[d.dept]} className="hover:text-blue-700 hover:underline underline-offset-2">{t(d.dept)}</Link>
                        : t(d.dept)}
                    </td>
                    <td className="text-center text-slate-500 font-semibold">{d.target}%</td>
                    <td className="text-center font-black" style={{ color: d.color }}>{d.actual}%</td>
                    <td className="text-center">
                      <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${statusClasses(gap >= 0 ? "good" : "critical")}`}>
                        {gap >= 0 ? "+" : ""}{gap}%
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <motion.div className="h-2 rounded-full" style={{ background: d.color }}
                            initial={{ width:0 }} animate={{ width:`${pct}%` }} transition={{ duration:0.8 }} />
                        </div>
                        <span className="text-[10px] font-black text-slate-700 flex-shrink-0">
                          ₹{d.burn}Cr/{pct}%
                        </span>
                      </div>
                    </td>
                    <td className="text-center font-semibold">{d.staff.toLocaleString()}</td>
                    <td className="text-center font-bold text-amber-600">{d.incidents}</td>
                    <td className="text-center">
                      <span className={`font-black ${STATUS[thresholdStatus(d.sla, [{ min:90, bucket:"good" }, { min:0, bucket:"warning" }])].text}`}>{d.sla}%</span>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Budget Allocation */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Budget Allocation by Category"
          subtitle={`Total ₹${TOTAL_BUDGET} Cr allocated · ₹${TOTAL_SPENT} Cr spent (${Math.round(TOTAL_SPENT/TOTAL_BUDGET*100)}%)`}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={BUDGET_CATEGORIES} layout="vertical" margin={{ top:5, right:60, left:10, bottom:0 }}>
              <CartesianGrid {...gridProps} horizontal={false} />
              <XAxis type="number" tick={axisTick} unit="Cr" />
              <YAxis type="category" dataKey="cat" tick={axisTick} width={120} tickFormatter={t} />
              <Tooltip content={<ChartTooltip formatter={(v, n) => [`₹${v} Cr`, n]} />} />
              <Legend wrapperStyle={legendStyle} formatter={(value) => t(value)} />
              <Bar dataKey="allocated" fill={chartColor(0)} fillOpacity={0.35} radius={[0,3,3,0]} name="Allocated" />
              <Bar dataKey="spent"     fill={chartColor(0)} radius={[0,3,3,0]} name="Spent" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Budget Share by Category" subtitle="Proportion of ₹1,200 Cr total Simhastha 2027 allocation">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={BUDGET_PIE} dataKey="value" nameKey="name"
                cx="50%" cy="50%" outerRadius={85} innerRadius={44}
                label={({ name, percent }) => `${t(name)} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}>
                {BUDGET_PIE.map((e, i) => <Cell key={i} fill={chartColor(i)} />)}
              </Pie>
              <Tooltip content={<ChartTooltip formatter={(v, n) => [`₹${v} Cr`, n]} />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 grid grid-cols-2 gap-1">
            {BUDGET_PIE.map((b, i) => (
              <div key={b.name} className="flex items-center gap-1.5 text-[9px]">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background:chartColor(i) }} />
                <span className="text-slate-600">{t(b.name)}: <strong>₹{b.allocated || b.value} Cr</strong></span>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Strategic Decisions Log */}
      <ChartCard title="Strategic Decision Log — Today"
        subtitle="High-level decisions by DCP / Collector / CM Office with impact and execution status">
        <p className="text-[10px] text-slate-400 mb-2">
          {t("Showing")} {STRATEGIC_DECISIONS.length} {t("most recent of")} {DECISIONS_TODAY} {t("decisions today")}
        </p>
        <div className="space-y-2">
          {STRATEGIC_DECISIONS.map((d, i) => (
            <motion.div key={d.id}
              initial={{ opacity:0, y:3 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.05 }}
              className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors flex-wrap"
            >
              <span className="font-mono text-[9px] text-slate-400 w-10 flex-shrink-0">{d.time}</span>
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                  <span className="font-mono text-[8px] text-slate-400">{d.id}</span>
                  <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full border ${statusClasses(impactBucket(d.impact))}`}>{t(d.impact)}</span>
                </div>
                <p className="text-xs font-semibold text-slate-800">{t(d.decision)}</p>
                <p className="text-[9px] text-slate-500 mt-0.5">{t("By:")} <strong className="text-blue-700">{t(d.by)}</strong></p>
              </div>
              <span className={`text-[8px] font-black px-2 py-1 rounded-full flex-shrink-0 ${statusClasses(decisionStatusBucket(d.status))}`}>{t(d.status)}</span>
            </motion.div>
          ))}
        </div>
      </ChartCard>

      {/* Shahi Snan Readiness + Policy Compliance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Shahi Snan Readiness Index"
          subtitle="Budget · staff · infrastructure readiness per upcoming Shahi Snan event">
          <div className="space-y-4">
            {READINESS.map((r, i) => (
              <motion.div key={r.event}
                initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.1 }}
                className="p-3 rounded-xl border border-slate-200">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div>
                    <p className="text-xs font-black text-slate-900">{t(r.event)}</p>
                    <div className="flex items-center gap-2 text-[9px] text-slate-500">
                      <Clock size={9} />{r.days} {t("days away")} · {t("Est. crowd:")} <strong className="text-blue-700">{r.crowd}</strong>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className={`text-xl font-black ${STATUS[thresholdStatus(r.readiness, [{ min:75, bucket:"good" }, { min:60, bucket:"warning" }, { min:0, bucket:"critical" }])].text}`}>
                      {r.readiness}%
                    </div>
                    <div className="text-[9px] text-slate-500">{t("overall")}</div>
                  </div>
                </div>
                {[
                  { label:"Budget", val:r.budget, color:chartColor(0) },
                  { label:"Staff",  val:r.staff,  color:chartColor(1) },
                  { label:"Infra",  val:r.infra,  color:chartColor(2) },
                ].map(m => (
                  <div key={m.label} className="mb-1.5">
                    <div className="flex justify-between text-[9px] mb-0.5">
                      <span className="text-slate-500">{t(m.label)}</span>
                      <span className="font-black" style={{ color:m.color }}>{m.val}%</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <motion.div className="h-1.5 rounded-full" style={{ background:m.color }}
                        initial={{ width:0 }} animate={{ width:`${m.val}%` }} transition={{ duration:0.8 }} />
                    </div>
                  </div>
                ))}
              </motion.div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Policy & Regulatory Compliance"
          subtitle="7 active compliance frameworks — score and status">
          <div className="space-y-2.5">
            {POLICY_COMPLIANCE.map((p, i) => {
              const bucket = p.status === "Review" ? "warning" : "good";
              return (
                <motion.div key={p.policy}
                  initial={{ opacity:0, x:4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                  className={`p-3 rounded-xl border ${bucket === "warning" ? `${STATUS.warning.border} ${STATUS.warning.bg}/20` : "border-slate-200"}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-slate-800">{t(p.policy)}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <motion.div className={`h-1.5 rounded-full ${STATUS[bucket].bar}`}
                            initial={{ width:0 }} animate={{ width:`${p.score}%` }} transition={{ duration:0.8 }} />
                        </div>
                        <span className="text-[10px] font-black text-slate-700">{p.score}%</span>
                      </div>
                    </div>
                    <span className={`text-[8px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full flex-shrink-0 ${statusClasses(bucket)}`}>
                      {t(p.status)}
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </ChartCard>
      </div>
    </div>
  );
}






